import { useEffect, useRef } from 'react';
import { LunchCardAPI, generateQRDataWithId } from '@/lib/lunchCardService';
import { formatSwedishPhone } from '@/lib/phoneUtils';
import { Building2 } from 'lucide-react';
import QRCode from 'qrcode';

interface CompanyQRCodeDisplayProps {
  card: LunchCardAPI;
  showInstructions?: boolean;
}

export const CompanyQRCodeDisplay = ({ card, showInstructions = false }: CompanyQRCodeDisplayProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Generate QR with card ID for company cards
    const qrData = generateQRDataWithId(card.id, 'company');
    
    QRCode.toCanvas(canvas, qrData, {
      width: 200,
      margin: 2,
      color: {
        dark: '#1a1a1a',
        light: '#FFFFFF',
      },
    });
  }, [card]);

  return (
    <div className="flex flex-col items-center">
      <div className="bg-card rounded-2xl shadow-warm-lg p-6 border border-border">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Building2 size={20} className="text-primary" />
          <span className="text-sm font-medium text-primary">Företagslunchkort</span>
        </div>

        <div className="bg-background p-4 rounded-xl mb-4">
          <canvas ref={canvasRef} className="mx-auto" />
        </div>

        <div className="text-center">
          <p className="font-medium text-foreground">{card.companyName || 'Företag'}</p>
          <p className="text-sm text-muted-foreground">{formatSwedishPhone(card.phoneNumber)}</p>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 px-6 py-4 rounded-xl bg-primary/10">
        <Building2 size={24} className="text-primary" />
        <div>
          <p className="text-sm text-muted-foreground">Luncher kvar</p>
          <p className="text-2xl font-bold text-primary">
            {card.remaining}
            {card.total && <span className="text-lg font-normal text-muted-foreground ml-1">/ {card.total}</span>}
          </p>
        </div>
      </div>

      {showInstructions && (
        <div className="mt-6 p-4 rounded-lg bg-secondary/50 text-center max-w-xs">
          <p className="text-sm text-muted-foreground">
            Dela denna QR-kod internt för att använda luncherna
          </p>
        </div>
      )}
    </div>
  );
};
